import Notification from 'components/ui/base/notification/Notification';
import { FormikProps } from 'formik';
import { getErrorMessage } from 'utils/getErrorMessage';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useDeclarationValidation from 'hooks/useDeclarationValidation';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Events } from 'utils/events.enum';
import { FormAction } from 'views/declarations/utils/form-utils';

interface Props {
    saveDeclaration: () => Promise<any>;
    saveProduct: Function;
    validateForm: () => Promise<any>;
    productsFormik: FormikProps<any>;
    refetchDeclaration: () => Promise<void>;
}

const useHandleFormAction = ({
    saveDeclaration: saveForm,
    saveProduct,
    validateForm,
    productsFormik,
    refetchDeclaration,
}: Props) => {
    const { t } = useTranslation('common');
    const { declarationValidation, setFormAction } = useDeclarationValidation({ formAction: null });
    const { hasFormDeclarationErrors } = useDeclarationFormErrors();
    const { hideGlobalOverlay, showGlobalOverlay } = useGlobalOverlay();

    const showSavingFailed = (error: unknown) => {
        console.error('Form action failed:', error);

        Notification({
            type: 'error',
            messageTitle: 'Saving failed.',
            description: getErrorMessage(error) || 'Something went wrong.',
            placement: 'top',
        });
    };

    // Whatever the save does, the overlay has to come down - a failure that leaves it behind
    // locks the user out of the form entirely. Callers that move on afterwards wait for the result.
    const saveOpenProduct = async (): Promise<{ saved: boolean }> => {
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: t('savingDeclaration') } });
        window.EventBus.publish(Events.SAVING_DECLARATION_START);

        try {
            // The save reports a failed request by resolving to false - it does not throw.
            const saved: boolean = await saveProduct(productsFormik.values);
            if (!saved) return { saved: false };

            if (hasFormDeclarationErrors) {
                await refetchDeclaration();
                await validateForm();
            }
            return { saved: true };
        } catch (error) {
            showSavingFailed(error);
            return { saved: false };
        } finally {
            hideGlobalOverlay();
            window.EventBus.publish(Events.SAVING_DECLARATION_END);
        }
    };

    useEffect(() => {
        if (declarationValidation.formAction === null) return;

        if (declarationValidation.formAction === FormAction.PRODUCT) {
            saveOpenProduct().finally(() => setFormAction(null));
            return;
        }

        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: t('savingDeclaration') } });

        window.EventBus.publish(Events.SAVING_DECLARATION_START);

        // Whatever the action does, the overlay has to come down and the form action has to be
        // cleared - a failure that leaves either behind locks the user out of the form entirely.
        saveForm()
            .catch(showSavingFailed)
            .finally(() => {
                hideGlobalOverlay();
                window.EventBus.publish(Events.SAVING_DECLARATION_END);

                setFormAction(null);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declarationValidation.formAction]);

    return { saveOpenProduct };
};

export default useHandleFormAction;
